// FieldError — the one inline error row. Icon + message, `role="alert"`.
//
// Extracted from FormField so that controls WITHOUT a FormField wrapper can
// show the identical treatment. Radio groups, chip multi-selects and
// whole-form messages all sit outside a labelled field, and each surface that
// hand-rolled its own `<p role="alert">` drifted a little: no icon, different
// spacing, different vertical rhythm. One component, one look.
//
// FormField renders this internally, so the two can never diverge again.
//
// --type-body-sm-strong, matching the helper text it REPLACES in that slot at
// --type-body-sm: same 14px step, same 1.55 leading, 600 instead of 400. The
// weight is the whole difference, so an error reads as the more urgent version
// of the line it stands in for rather than as a different kind of text. Both
// were 12px `text-xs` and neither named a token.
//
// It is a FILLED BLOCK on --danger-bg, not loose text: full width of the
// control above it (a plain block element, so the field's own column gives it
// that), --radius-sm to echo the field's corners, no border. The tint is what
// ties the message to the red-ringed field it belongs to — as bare text it read
// as another line of helper copy that happened to be red.
//
// Top-aligned, not centred. A one-line message looks the same either way, but
// these wrap: centred, the icon drifts to the middle of a two-line block and
// stops pointing at the sentence. The 4px nudge sits it on the first line's
// optical centre (14px glyph in a 21.7px line box).

export interface FieldErrorProps {
    /** The message. Nothing renders when this is empty. */
    children?: string;
    /** Set when a FormField owns the aria-describedby wiring. */
    id?: string;
}

export function FieldError({ children, id }: FieldErrorProps) {
    if (!children) return null;
    return (
        <div
            id={id}
            role="alert"
            className="mt-2 flex items-start gap-2 rounded-[var(--radius-sm)] bg-danger-bg px-4 py-2 [font:var(--type-body-sm-strong)] text-danger"
        >
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none" className="mt-1 shrink-0" aria-hidden>
                <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.25" />
                <path d="M6 3.2v3.4M6 8.4v.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {children}
        </div>
    );
}
